import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import useFetch from "@/hooks/useFetch";
import {
  fetchMyLeakedFundsHistory,
  fetchUserLeakedFundsHistory,
} from "@/services/funds-movement";
import { getDateFromWeekOfYear } from "@/utils/getDateFromWeekOfYear";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Separator } from "../ui/Separator";
import WithLoader from "../ui/WithLoader";
import DebtStatusBadge from "./DebtStatusBadge";
import { Button } from "../ui/Button";

const INITIAL_SIZE = 4;

const LeakedFundsHistoryRow = ({ item }) => {
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <span className="text-muted-foreground">
        {getDateFromWeekOfYear(item.year, item.weekOfYear)} {item.year}
      </span>
      <DebtStatusBadge debtValue={item.leakedFunds} />
    </div>
  );
};

const LeakedFundsHistoryPopover = ({ trigger, username }) => {
  const { t } = useTranslation("sub-user");
  const { t: tFunds } = useTranslation("funds-movement");
  const [isOpen, setIsOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [allItems, setAllItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const fetchPage = (p, size) =>
    username
      ? fetchUserLeakedFundsHistory(username, p, size)
      : fetchMyLeakedFundsHistory(p, size);

  const { isLoading } = useFetch(
    async () => {
      const data = await fetchPage(0, INITIAL_SIZE);
      setAllItems(data?.content || []);
      setHasMore((data?.content?.length || 0) < (data?.totalElements || 0));
      setPage(0);
      return data;
    },
    [username],
    isOpen
  );

  const handleLoadMore = async () => {
    const nextPage = page + 1;
    setIsLoadingMore(true);
    try {
      const data = await fetchPage(nextPage, INITIAL_SIZE);
      const newItems = [...allItems, ...(data?.content || [])];
      setAllItems(newItems);
      setHasMore(newItems.length < (data?.totalElements || 0));
      setPage(nextPage);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const title = username
    ? tFunds("user.table_title", { username })
    : t("leaked_funds_history");

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>

      <PopoverContent align="end" className="min-w-[280px]">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="font-semibold">{title}</span>
          </div>
          <Separator />

          <WithLoader isLoading={isLoading} className="my-14">
            {allItems.length === 0 && !isLoading ? (
              <p className="text-sm text-muted-foreground text-center py-2">
                {t("no_leaked_history")}
              </p>
            ) : (
              allItems.map((item, index) => (
                <LeakedFundsHistoryRow
                  item={item}
                  key={`${item.year}-${item.weekOfYear}-${index}`}
                />
              ))
            )}
          </WithLoader>

          {hasMore && allItems.length > 0 && (
            <Button
              variant="link"
              className="w-fit mx-auto"
              onClick={handleLoadMore}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? "..." : t("view_more")}
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default LeakedFundsHistoryPopover;
